import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from './ui';

/** Size limits of a resizable part of the window, in pixels. */
export interface PanelSize {
  /** localStorage key the size is remembered under (per browser). */
  key: string;
  initial: number;
  min: number;
  max: number;
}

const clampTo = (v: number, min: number, max: number) => Math.round(Math.min(Math.max(min, max), Math.max(min, v)));

function saved(p: PanelSize): number {
  try {
    const v = Number(localStorage.getItem(p.key));
    if (v) return clampTo(v, p.min, p.max);
  } catch {
    /* storage unavailable */
  }
  return p.initial;
}

/**
 * A size the user can drag (see Splitter), remembered per browser. `max` may
 * depend on the window (it is applied on every render, so a smaller window
 * shrinks the part without forgetting the size it had).
 */
export function usePanelSize(p: PanelSize): [number, (size: number, persist?: boolean) => void] {
  const [size, setSize] = useState(() => saved(p));
  const set = useCallback(
    (v: number, persist = true) => {
      setSize(v);
      if (!persist) return;
      try {
        if (v === p.initial) localStorage.removeItem(p.key);
        else localStorage.setItem(p.key, String(Math.round(v)));
      } catch {
        /* private mode: keep it for this session only */
      }
    },
    [p.key, p.initial],
  );
  return [clampTo(size, p.min, p.max), set];
}

/**
 * Drag handle between two parts of the window. `edge` says which side of the
 * resized part the handle sits on: dragging away from that part makes it
 * bigger. Arrow keys resize it too, and a double-click puts it back to its
 * initial size.
 */
export function Splitter({
  size,
  onSize,
  limits,
  edge,
  label,
  className,
}: {
  size: number;
  onSize: (size: number, persist?: boolean) => void;
  limits: PanelSize;
  edge: 'left' | 'right' | 'top' | 'bottom';
  label: string;
  className?: string;
}) {
  const drag = useRef<{ at: number; size: number; last: number } | null>(null);
  const [active, setActive] = useState(false);
  const vertical = edge === 'left' || edge === 'right';
  // Moving the pointer right (or down) grows a part whose handle is on its right (or bottom) edge.
  const sign = edge === 'right' || edge === 'bottom' ? 1 : -1;
  const clamp = (v: number) => clampTo(v, limits.min, limits.max);

  useEffect(() => {
    if (!active) return;
    document.body.style.cursor = vertical ? 'col-resize' : 'row-resize';
    return () => {
      document.body.style.cursor = '';
    };
  }, [active, vertical]);

  return (
    <div
      role="separator"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-label={label}
      aria-valuenow={size}
      aria-valuemin={limits.min}
      aria-valuemax={limits.max}
      tabIndex={0}
      title={`Drag to resize · double-click to reset`}
      data-splitter={limits.key}
      className={cx(
        'group relative z-10 shrink-0 touch-none outline-none',
        vertical ? 'w-1 cursor-col-resize' : 'h-1.5 cursor-row-resize',
        className,
      )}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { at: vertical ? e.clientX : e.clientY, size, last: size };
        setActive(true);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        d.last = clamp(d.size + sign * ((vertical ? e.clientX : e.clientY) - d.at));
        onSize(d.last, false);
      }}
      onPointerUp={() => {
        const d = drag.current;
        drag.current = null;
        setActive(false);
        if (d && d.last !== d.size) onSize(d.last);
      }}
      onPointerCancel={() => {
        drag.current = null;
        setActive(false);
      }}
      onDoubleClick={() => onSize(clamp(limits.initial))}
      onKeyDown={(e) => {
        const grow = vertical ? (edge === 'right' ? 'ArrowRight' : 'ArrowLeft') : edge === 'bottom' ? 'ArrowDown' : 'ArrowUp';
        const shrink = vertical ? (edge === 'right' ? 'ArrowLeft' : 'ArrowRight') : edge === 'bottom' ? 'ArrowUp' : 'ArrowDown';
        const step = e.shiftKey ? 64 : 16;
        if (e.key === grow) onSize(clamp(size + step));
        else if (e.key === shrink) onSize(clamp(size - step));
        else if (e.key === 'Home') onSize(clamp(limits.min));
        else if (e.key === 'End') onSize(clamp(limits.max));
        else return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* A wider invisible grip, and a line that lights up while hovered or dragged. */}
      <div className={cx('absolute', vertical ? '-inset-x-1.5 inset-y-0' : '-inset-y-1 inset-x-0')} />
      <div
        className={cx(
          'pointer-events-none absolute transition-colors group-hover:bg-accent/60 group-focus-visible:bg-accent',
          vertical ? 'inset-y-0 left-1/2 w-0.5 -translate-x-1/2' : 'inset-x-0 top-1/2 h-0.5 -translate-y-1/2',
          active && 'bg-accent',
        )}
      />
    </div>
  );
}
